import { Clock, Loader2 } from 'lucide-react';
import { DAY_NAMES, bestTimesSourceNote, useBestTimesForAnalytics } from '../lib/bestTimes';

/** Monday first, as in the queue editor. */
const ROW_ORDER = [1, 2, 3, 4, 5, 6, 0];
/** Night hours carry no useful signal and would squeeze the grid on mobile. */
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6);

const pad = (n: number) => String(n).padStart(2, '0');
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Weekday × hour heatmap of when the selected account(s) get most engagement. */
export function BestTimesCard({ accountId }: { accountId?: string }) {
  const { data, isLoading } = useBestTimesForAnalytics(accountId);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1">
        <Clock size={16} className="text-slate-400" />
        <h2 className="text-sm font-semibold text-slate-700">Mejores horas para publicar</h2>
      </div>

      {isLoading || !data ? (
        <div className="py-10 flex items-center justify-center gap-2 text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin" /> Calculando…
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-4">{bestTimesSourceNote(data)}</p>

          <div className="flex flex-wrap gap-x-8 gap-y-3 mb-5">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Mejores días</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {data.bestDays.slice(0, 3).map((d) => capitalize(DAY_NAMES[d.dayOfWeek]!)).join(', ')}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Mejores horas</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {data.bestHours.map((h) => (
                  <span
                    key={h.hour}
                    className="inline-flex items-center rounded-full bg-brand-50 text-brand-700 text-xs font-semibold px-2.5 py-0.5"
                  >
                    {pad(h.hour)}:00
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div role="table" aria-label="Rendimiento por día y hora" className="space-y-1">
            {ROW_ORDER.map((dow) => (
              <div key={dow} role="row" className="flex items-center gap-1">
                <div role="rowheader" className="w-9 shrink-0 text-[11px] text-slate-500">
                  {capitalize(DAY_NAMES[dow]!.slice(0, 3))}
                </div>
                {HOURS.map((h) => {
                  const score = data.grid[dow]?.[h] ?? 0;
                  return (
                    <div
                      key={h}
                      role="cell"
                      title={`${capitalize(DAY_NAMES[dow]!)} ${pad(h)}:00 · ${score}/100`}
                      className="h-6 flex-1 min-w-0 rounded-[3px]"
                      style={{ backgroundColor: `rgba(1, 111, 255, ${0.06 + 0.94 * (score / 100) ** 1.6})` }}
                    />
                  );
                })}
              </div>
            ))}
            <div className="flex items-center gap-1" aria-hidden>
              <div className="w-9 shrink-0" />
              {HOURS.map((h) => (
                <div key={h} className="flex-1 min-w-0 text-center text-[10px] text-slate-400">
                  {h % 3 === 0 ? h : ''}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-slate-400">
            Peor
            <span className="h-2.5 w-20 rounded-full bg-gradient-to-r from-brand-50 to-brand-600" />
            Mejor
          </div>
        </>
      )}
    </div>
  );
}
